import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input } from "../../components/ui";
import Alert from "../../components/ui/Alert/Alert";
import { EmptyState } from "../../components/ui";
import { useFormValidation } from "../../hooks";
import { commonValidationRules } from "../../utils/validation";
import { AuthService } from "../../services/authService";

interface ResetForm {
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [alert, setAlert] = React.useState<{ type: "success" | "error"; message: string } | null>(
    null
  );

  const form = useFormValidation<ResetForm>({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationRules: {
      newPassword: commonValidationRules.password,
      confirmPassword: {
        required: true,
        custom: (value: unknown, allValues?: Record<string, unknown>): string | null => {
          if (!value) return "Confirm Password is required";
          if (value !== allValues?.newPassword) return "Passwords do not match";
          return null;
        },
      },
    },
    onSubmit: async (values) => {
      const { ok, data } = await AuthService.resetPassword(token as string, values.newPassword);
      if (ok) {
        setAlert({ type: "success", message: "Your password has been reset. Redirecting..." });
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setAlert({ type: "error", message: data.error || data.message || "Reset failed." });
      }
    },
  });

  const shell = (children: React.ReactNode) => (
    <div className="bg-zinc-950 text-white min-h-screen flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-8 py-4 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-sm">
        <a href="/" className="flex items-center gap-3 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-8 h-8 object-contain" />
          <span className="text-xl font-light tracking-widest">
            Role<span className="text-amber-400 font-normal">Weave</span>
          </span>
        </a>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pt-20">{children}</main>
      <footer className="py-10 px-8 border-t border-zinc-900 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-5 h-5 object-contain opacity-70" />
          <span className="text-zinc-500 text-xs tracking-widest">
            Role<span className="text-amber-400/80">Weave</span>
          </span>
        </div>
        <p className="text-zinc-500 text-xs">© 2026 RoleWeave. All rights reserved.</p>
      </footer>
    </div>
  );

  if (!token) {
    return shell(
      <EmptyState
        title="Invalid reset link"
        description="This password reset link is invalid or has expired. Please request a new one."
        icon="error"
        size="md"
        action={
          <Button onClick={() => navigate("/forgot-password")} variant="primary">
            Request new link
          </Button>
        }
      />
    );
  }

  return shell(
    <div className="w-full max-w-md">
      <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase text-center mb-3">
        Account recovery
      </p>
      <h1 className="text-3xl md:text-4xl font-thin text-center text-white mb-10 tracking-wide">
        Reset password
      </h1>

      <div className="border border-zinc-800 bg-zinc-900 p-8">
        {alert && (
          <div className="mb-4">
            <Alert
              variant={alert.type === "success" ? "success" : "error"}
              dismissible
              onDismiss={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          </div>
        )}
        <form onSubmit={form.handleSubmit}>
          <div className="mb-4">
            <Input
              type="password"
              id="newPassword"
              label="New password"
              value={form.values.newPassword}
              onChange={form.handleChange("newPassword")}
              onBlur={form.handleBlur("newPassword")}
              error={form.errors.newPassword}
              fullWidth
              variant="filled"
            />
          </div>
          <div className="mb-6">
            <Input
              type="password"
              id="confirmPassword"
              label="Confirm password"
              value={form.values.confirmPassword}
              onChange={form.handleChange("confirmPassword")}
              onBlur={form.handleBlur("confirmPassword")}
              error={form.errors.confirmPassword}
              fullWidth
              variant="filled"
            />
          </div>
          <Button type="submit" variant="primary" size="md" fullWidth disabled={form.isSubmitting}>
            {form.isSubmitting ? "Resetting..." : "Reset password"}
          </Button>
        </form>
        <div className="mt-6 text-center text-sm text-zinc-400">
          <Link
            to="/login"
            className="text-amber-400 hover:text-amber-300 transition-colors duration-200"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
